import { useState, useEffect } from "react";
import { db } from "./firebase";
import { collection, getDocs, query, where, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth } from "./firebase";
import {
  createChallenge,
  updateChallenge,
  deleteChallenge,
  getAllChallenges,
  getChallengeParticipantProgress,
} from "./challenges";

const SOURCE_TYPES = [
  { value: "workout_logged", label: "Workout logged in app" },
  { value: "side_quest_any", label: "Any side quest completed" },
  { value: "side_quest_specific", label: "Specific side quest (select below)" },
  { value: "habit_any", label: "Any habit checked off" },
  { value: "habit_specific", label: "Specific habit category (select below)" },
  { value: "checkin", label: "Daily check-in" },
];

const HABIT_CATEGORIES = [
  { value: "nutrition", label: "🥗 Nutrition" },
  { value: "movement", label: "💪 Movement" },
  { value: "mindset", label: "🧠 Mindset" },
];

const emptyForm = () => ({
  title: "",
  description: "",
  prize: "",
  type: "count",
  target: "",
  startDate: "",
  endDate: "",
  visibility: "public",
  invitedUsers: [],
  metric: {
    label: "",
    unit: "",
    trackingMode: "manual",
    sources: [],
    maxPerDay: 1,
  },
});

export default function AdminChallenges() {
  const [challenges, setChallenges] = useState([]);
  const [momentumUsers, setMomentumUsers] = useState([]);
  const [sideQuests, setSideQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = list view, obj = create/edit
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [participantProgress, setParticipantProgress] = useState({});
  const [editingScore, setEditingScore] = useState(null); // { challengeId, userId, value }
  const [savingScore, setSavingScore] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    const [cList, uSnap, gcSnap] = await Promise.all([
      getAllChallenges(),
      getDocs(query(collection(db, "users"), where("programTag", "==", "momentum"))),
      getDoc(doc(db, "appConfig", "gameContent")),
    ]);
    setChallenges(cList);
    setMomentumUsers(
      uSnap.docs
        .map((d) => ({ uid: d.id, name: d.data().name || d.data().displayName || d.id, avatar: d.data().avatar || "" }))
        .filter((u) => !uSnap.docs.find((d) => d.id === u.uid)?.data()?.isAdmin)
        .sort((a, b) => a.name.localeCompare(b.name))
    );
    if (gcSnap.exists()) {
      setSideQuests((gcSnap.data().sideQuests || []).filter((q) => q.title));
    }
    setLoading(false);
  }

  async function loadParticipants(challengeId) {
    if (participantProgress[challengeId]) return;
    const progress = await getChallengeParticipantProgress(challengeId);
    setParticipantProgress((prev) => ({ ...prev, [challengeId]: progress }));
  }

  async function saveScore(challengeId, userId, newTotal) {
    setSavingScore(true);
    try {
      await setDoc(
        doc(db, "challengeProgress", `${challengeId}_${userId}`),
        { total: Number(newTotal), dailyLog: {}, lastUpdated: serverTimestamp() },
        { merge: true }
      );
      setParticipantProgress((prev) => ({
        ...prev,
        [challengeId]: (prev[challengeId] || []).map((p) =>
          p.userId === userId ? { ...p, total: Number(newTotal) } : p
        ),
      }));
      setEditingScore(null);
    } finally {
      setSavingScore(false);
    }
  }

  function openCreate() {
    setForm(emptyForm());
    setEditingId(null);
  }

  function openEdit(c) {
    setForm({
      title: c.title || "",
      description: c.description || "",
      prize: c.prize || "",
      type: c.type || "count",
      target: c.target || "",
      startDate: c.startDate || "",
      endDate: c.endDate || "",
      visibility: c.visibility || "public",
      invitedUsers: c.invitedUsers || [],
      metric: c.metric || { label: "", unit: "", trackingMode: "manual", sources: [], maxPerDay: 1 },
    });
    setEditingId(c.id);
  }

  function setF(key, val) {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  function setMetric(key, val) {
    setForm((prev) => ({ ...prev, metric: { ...prev.metric, [key]: val } }));
  }

  function addSource() {
    setMetric("sources", [
      ...(form.metric.sources || []),
      { type: "workout_logged", maxPerDay: 1, questTitle: "" },
    ]);
  }

  function updateSource(idx, key, val) {
    const sources = [...(form.metric.sources || [])];
    sources[idx] = { ...sources[idx], [key]: val };
    setMetric("sources", sources);
  }

  function removeSource(idx) {
    const sources = [...(form.metric.sources || [])];
    sources.splice(idx, 1);
    setMetric("sources", sources);
  }

  function toggleInvite(uid) {
    setF(
      "invitedUsers",
      form.invitedUsers.includes(uid)
        ? form.invitedUsers.filter((u) => u !== uid)
        : [...form.invitedUsers, uid]
    );
  }

  async function handleSave() {
    if (!form.title.trim() || !form.startDate || !form.endDate) return;
    setSaving(true);
    try {
      const adminUid = auth.currentUser?.uid;
      if (editingId) {
        await updateChallenge(editingId, { ...form });
      } else {
        await createChallenge(form, adminUid);
      }
      await loadAll();
      setForm(null);
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    await deleteChallenge(id);
    setDeleteConfirm(null);
    setExpanded(null);
    await loadAll();
  }

  const statusColor = { active: "#16a34a", upcoming: "#d97706", completed: "#6b7280" };
  const typeEmoji = { count: "🏆", race: "🏁", collective: "🤝" };

  if (loading) return <div style={{ color: "#4b6b8a", padding: "20px" }}>Loading challenges…</div>;

  // ── Form view ─────────────────────────────────────────────────────────────
  if (form) {
    return (
      <div style={{ maxWidth: "680px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
          <button onClick={() => { setForm(null); setEditingId(null); }}
            style={{ padding: "8px 14px", borderRadius: "10px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", cursor: "pointer" }}>
            ← Back
          </button>
          <h3 style={{ margin: 0, color: "#1e3a5f" }}>{editingId ? "Edit Challenge" : "Create Challenge"}</h3>
        </div>

        <Section label="Basics">
          <Field label="Title *">
            <input value={form.title} onChange={(e) => setF("title", e.target.value)}
              style={inp} placeholder="April Workout Battle" />
          </Field>
          <Field label="Description">
            <textarea value={form.description} onChange={(e) => setF("description", e.target.value)}
              rows={2} style={{ ...inp, resize: "vertical" }} placeholder="Who can log the most workouts this month?" />
          </Field>
          <Field label="Prize / Stakes">
            <input value={form.prize} onChange={(e) => setF("prize", e.target.value)}
              style={inp} placeholder="Loser buys dinner" />
          </Field>
        </Section>

        <Section label="Challenge Rules">
          <Field label="Type">
            <div style={{ display: "flex", gap: "10px" }}>
              {[["count", "🏆 Most (Count)"], ["race", "🏁 Race (First to)"], ["collective", "🤝 Collective (Group goal)"]].map(([v, l]) => (
                <label key={v} style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px", color: "#1e3a5f" }}>
                  <input type="radio" checked={form.type === v} onChange={() => setF("type", v)} />
                  {l}
                </label>
              ))}
            </div>
          </Field>
          {(form.type === "race" || form.type === "collective") && (
            <Field label={form.type === "race" ? "Target (first to reach)" : "Group target"}>
              <input type="number" min="1" value={form.target} onChange={(e) => setF("target", e.target.value)}
                style={{ ...inp, width: "120px" }} placeholder="20" />
            </Field>
          )}
          <div style={{ display: "flex", gap: "14px" }}>
            <Field label="Start Date *">
              <input type="date" value={form.startDate} onChange={(e) => setF("startDate", e.target.value)} style={inp} />
            </Field>
            <Field label="End Date *">
              <input type="date" value={form.endDate} onChange={(e) => setF("endDate", e.target.value)} style={inp} />
            </Field>
          </div>
        </Section>

        <Section label="Who Can Join">
          <Field label="Visibility">
            <div style={{ display: "flex", gap: "16px" }}>
              {[["public", "🌐 Public (anyone in Momentum)"], ["private", "🔒 Private (invite only)"]].map(([v, l]) => (
                <label key={v} style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px", color: "#1e3a5f" }}>
                  <input type="radio" checked={form.visibility === v} onChange={() => setF("visibility", v)} />
                  {l}
                </label>
              ))}
            </div>
          </Field>
          {form.visibility === "private" && (
            <Field label="Select participants">
              <div style={{ display: "grid", gap: "6px", maxHeight: "200px", overflowY: "auto", border: "1.5px solid #bfdbfe", borderRadius: "10px", padding: "10px", background: "#f5faff" }}>
                {momentumUsers.map((u) => (
                  <label key={u.uid} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", color: "#1e3a5f" }}>
                    <input type="checkbox" checked={form.invitedUsers.includes(u.uid)} onChange={() => toggleInvite(u.uid)} />
                    {u.name}
                  </label>
                ))}
                {momentumUsers.length === 0 && <span style={{ color: "#6b7280", fontSize: "12px" }}>No momentum users found</span>}
              </div>
            </Field>
          )}
        </Section>

        <Section label="What Counts">
          <div style={{ display: "flex", gap: "14px", marginBottom: "14px" }}>
            <Field label='Metric label (e.g. "workouts")'>
              <input value={form.metric.label} onChange={(e) => setMetric("label", e.target.value)}
                style={inp} placeholder="workouts" />
            </Field>
            <Field label='Unit singular (e.g. "workout")'>
              <input value={form.metric.unit} onChange={(e) => setMetric("unit", e.target.value)}
                style={inp} placeholder="workout" />
            </Field>
          </div>
          <Field label="Tracking">
            <div style={{ display: "flex", gap: "16px" }}>
              {[["manual", "✏️ Manual (users log it themselves)"], ["auto", "⚡ Auto (tracks app activity)"]].map(([v, l]) => (
                <label key={v} style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px", color: "#1e3a5f" }}>
                  <input type="radio" checked={form.metric.trackingMode === v} onChange={() => setMetric("trackingMode", v)} />
                  {l}
                </label>
              ))}
            </div>
          </Field>

          {form.metric.trackingMode === "auto" && (
            <div style={{ marginTop: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px" }}>Auto-tracking sources</span>
                <button onClick={addSource} style={{ padding: "5px 12px", borderRadius: "8px", border: "2px solid #2563eb", background: "#eff6ff", color: "#2563eb", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>
                  + Add Source
                </button>
              </div>
              {(form.metric.sources || []).length === 0 && (
                <p style={{ color: "#6b7280", fontSize: "12px", margin: 0 }}>No sources yet — add at least one.</p>
              )}
              {(form.metric.sources || []).map((src, idx) => (
                <div key={idx} style={{ background: "#f0f9ff", borderRadius: "10px", padding: "10px 12px", border: "1px solid #bfdbfe", marginBottom: "8px", display: "flex", gap: "10px", alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 200px" }}>
                    <label style={{ fontSize: "11px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Counts when:</label>
                    <select value={src.type} onChange={(e) => updateSource(idx, "type", e.target.value)} style={{ ...inp, fontSize: "12px" }}>
                      {SOURCE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  {src.type === "side_quest_specific" && (
                    <div style={{ flex: "1 1 150px" }}>
                      <label style={{ fontSize: "11px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Which side quest:</label>
                      <select value={src.questTitle || ""} onChange={(e) => updateSource(idx, "questTitle", e.target.value)} style={{ ...inp, fontSize: "12px" }}>
                        <option value="">— select —</option>
                        {sideQuests.map((q) => (
                          <option key={q.title} value={q.title}>{q.emoji ? `${q.emoji} ` : ""}{q.title}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {src.type === "habit_specific" && (
                    <div style={{ flex: "1 1 150px" }}>
                      <label style={{ fontSize: "11px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Which category:</label>
                      <select value={src.category || ""} onChange={(e) => updateSource(idx, "category", e.target.value)} style={{ ...inp, fontSize: "12px" }}>
                        <option value="">— select —</option>
                        {HABIT_CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div style={{ width: "100px" }}>
                    <label style={{ fontSize: "11px", color: "#6b7280", display: "block", marginBottom: "4px" }}>Max/day:</label>
                    <input type="number" min="1" max="99" value={src.maxPerDay} onChange={(e) => updateSource(idx, "maxPerDay", Number(e.target.value))}
                      style={{ ...inp, fontSize: "12px" }} />
                  </div>
                  <button onClick={() => removeSource(idx)}
                    style={{ marginTop: "18px", padding: "4px 10px", borderRadius: "8px", border: "1px solid #fecaca", background: "#fff5f5", color: "#dc2626", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>
                    ✕
                  </button>
                </div>
              ))}
              <Field label="Overall max count per day (across all sources)">
                <input type="number" min="1" value={form.metric.maxPerDay} onChange={(e) => setMetric("maxPerDay", Number(e.target.value))}
                  style={{ ...inp, width: "100px" }} />
              </Field>
            </div>
          )}
        </Section>

        <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
          <button onClick={() => { setForm(null); setEditingId(null); }}
            style={{ flex: 1, padding: "12px", borderRadius: "12px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !form.title.trim() || !form.startDate || !form.endDate}
            style={{ flex: 2, padding: "12px", borderRadius: "12px", border: "none", background: saving ? "#93c5fd" : "#2563eb", color: "#fff", fontWeight: "bold", cursor: "pointer" }}>
            {saving ? "Saving…" : editingId ? "Save Changes" : "Create Challenge"}
          </button>
        </div>
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: "700px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
        <h3 style={{ margin: 0, color: "#1e3a5f" }}>🏆 Challenges</h3>
        <button onClick={openCreate}
          style={{ padding: "10px 18px", borderRadius: "12px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", cursor: "pointer" }}>
          + New Challenge
        </button>
      </div>

      {challenges.length === 0 && (
        <div style={{ color: "#6b7280", fontSize: "14px", textAlign: "center", padding: "40px" }}>
          No challenges yet. Create one to get started.
        </div>
      )}

      {challenges.map((c) => (
        <div key={c.id} style={{ background: "#f8fbff", borderRadius: "16px", border: "2px solid #bfdbfe", marginBottom: "12px", overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", cursor: "pointer" }}
            onClick={async () => {
              if (expanded === c.id) { setExpanded(null); return; }
              setExpanded(c.id);
              await loadParticipants(c.id);
            }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "18px" }}>{typeEmoji[c.type] || "🏆"}</span>
                <span style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "15px" }}>{c.title}</span>
                <span style={{ padding: "2px 8px", borderRadius: "999px", background: statusColor[c.status] + "22", color: statusColor[c.status], fontSize: "11px", fontWeight: "bold", textTransform: "uppercase" }}>
                  {c.status}
                </span>
                {c.visibility === "private" && <span style={{ fontSize: "11px", color: "#6b7280" }}>🔒 Private</span>}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                {c.startDate} → {c.endDate} · {(c.participants || []).length} participant{(c.participants || []).length !== 1 ? "s" : ""}
                {c.prize && <span> · 🎁 {c.prize}</span>}
              </div>
            </div>
            <span style={{ color: "#6b7280", fontSize: "20px" }}>{expanded === c.id ? "▲" : "▼"}</span>
          </div>

          {expanded === c.id && (
            <div style={{ borderTop: "1.5px solid #dbeafe", padding: "14px 16px", background: "#f0f9ff" }}>
              {c.description && <p style={{ margin: "0 0 10px", color: "#4b6b8a", fontSize: "13px" }}>{c.description}</p>}
              <div style={{ fontSize: "13px", color: "#1e3a5f", marginBottom: "12px" }}>
                <strong>Type:</strong> {c.type} {c.type !== "count" && c.target ? `· Target: ${c.target} ${c.metric?.unit || ""}` : ""}
                {" · "}<strong>Tracking:</strong> {c.metric?.trackingMode || "manual"}
                {" · "}<strong>Metric:</strong> {c.metric?.label || "—"}
              </div>

              {(participantProgress[c.id] || []).length > 0 && (
                <div style={{ marginBottom: "12px" }}>
                  <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px", marginBottom: "8px" }}>Leaderboard</div>
                  {(participantProgress[c.id] || [])
                    .sort((a, b) => (b.total || 0) - (a.total || 0))
                    .map((p, i) => {
                      const isEditing = editingScore?.challengeId === c.id && editingScore?.userId === p.userId;
                      return (
                        <div key={p.userId} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                          <span style={{ fontSize: "13px", width: "20px", color: "#6b7280" }}>#{i + 1}</span>
                          <span style={{ fontSize: "13px", flex: 1, color: "#1e3a5f" }}>{p.userName || p.userId}</span>
                          {isEditing ? (
                            <>
                              <input
                                type="number"
                                min="0"
                                value={editingScore.value}
                                onChange={(e) => setEditingScore((s) => ({ ...s, value: e.target.value }))}
                                style={{ width: "54px", padding: "3px 6px", borderRadius: "6px", border: "1.5px solid #bfdbfe", fontSize: "13px", textAlign: "center" }}
                              />
                              <button onClick={() => saveScore(c.id, p.userId, editingScore.value)} disabled={savingScore} style={{ padding: "3px 8px", borderRadius: "6px", border: "none", background: "#2563eb", color: "#fff", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>
                                {savingScore ? "…" : "Save"}
                              </button>
                              <button onClick={() => setEditingScore(null)} style={{ padding: "3px 6px", borderRadius: "6px", border: "1px solid #d1d5db", background: "#f9fafb", color: "#374151", fontSize: "12px", cursor: "pointer" }}>✕</button>
                            </>
                          ) : (
                            <>
                              <span style={{ fontWeight: "bold", color: "#2563eb", fontSize: "14px" }}>{p.total || 0} {c.metric?.unit || ""}</span>
                              <button onClick={() => setEditingScore({ challengeId: c.id, userId: p.userId, value: p.total || 0 })} title="Correct score" style={{ background: "none", border: "none", color: "#9ca3af", fontSize: "13px", cursor: "pointer", padding: "0 2px" }}>✏️</button>
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}

              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <button onClick={() => openEdit(c)}
                  style={{ padding: "7px 14px", borderRadius: "10px", border: "2px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}>
                  Edit
                </button>
                {deleteConfirm === c.id ? (
                  <span style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <span style={{ fontSize: "12px", color: "#dc2626" }}>Delete this challenge?</span>
                    <button onClick={() => handleDelete(c.id)}
                      style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid #fecaca", background: "#fee2e2", color: "#dc2626", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>Yes, delete</button>
                    <button onClick={() => setDeleteConfirm(null)}
                      style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid #d1d5db", background: "#f9fafb", color: "#374151", fontWeight: "bold", fontSize: "12px", cursor: "pointer" }}>Cancel</button>
                  </span>
                ) : (
                  <button onClick={() => setDeleteConfirm(c.id)}
                    style={{ padding: "7px 14px", borderRadius: "10px", border: "2px solid #fecaca", background: "#fff5f5", color: "#dc2626", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ background: "#f8fbff", borderRadius: "14px", border: "1.5px solid #bfdbfe", padding: "16px", marginBottom: "14px" }}>
      <div style={{ fontWeight: "bold", color: "#1e3a5f", fontSize: "13px", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      <label style={{ display: "block", fontSize: "12px", color: "#4b6b8a", fontWeight: "bold", marginBottom: "5px" }}>{label}</label>
      {children}
    </div>
  );
}

const inp = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: "8px",
  border: "1.5px solid #bfdbfe",
  background: "#f5faff",
  color: "#1e3a5f",
  fontSize: "13px",
  boxSizing: "border-box",
};
